<svelte:options tag={null} />
<script lang="typescript">
  import { onMount } from "svelte/internal";
  import { isPermissionPresent } from '@everymatrix/retail-helpers';

  import '@everymatrix/em-card';
  import '@everymatrix/em-accordion';
  import type { 
    OmPlayerLimits, 
    OmSportsLimits, 
    Sport 
  } from "./types/SportLimit.types";

  export let src: string;
  export let playerid: string;
  export let authHeaders;
  export let currency;
  let permissions;
  permissions = [];

  // API Data
  let userLimits;
  let paymentRules;
  let isLoading: any;

  let tabs = ['User Limits', 'Payment Limits', 'Sports Limits'];
  let currentTab = tabs[0];

  // Validation Params
  let reqSuccesfullLimit;
  let reqSaveError;

  // Sports
  let allSports;
  let sportsLimits: OmPlayerLimits;

  const handleTab = (e) => {
    e = e || window.event;
    let target = e.target || e.srcElement;
    let text = target.textContent || target.innerText;
    currentTab = text;
    return currentTab;
  };

  const getV2Url = (src: string) => {
    const url = new URL('/v2/player/', src)
    return url.href;
  }

  const fetchUserLimits = async (src: string, headers) => {
    try {
      const res = await fetch(src, { headers: { ...headers } });
      const data = await res.json();

      res.ok ? userLimits = {...data} : null;

    } catch (err) {
      throw new Error(`Api failed with: ${err}`);
    }
  };

  const fetchAllSports = async (src:string) => {
    try {
      const res = await fetch(src);
      const { sports }:{sports: Sport[]} = await res.json();
      if(res.ok){
        sports.sort((first, second) => {
          if(first.name > second.name){
            return 1;
          }
          else if(first.name < second.name){
            return -1;
          }
          return 0;
        })
        return sports;
      }else{
        throw new Error("Failed to fetch List of sports");
      }
    } catch (error) {
      console.error(error)
    }
  };

  const fetchSportsLimits = async (src: string, playerid:string, headers: HeadersInit) => {
    try {
      isLoading = true;
      allSports = await fetchAllSports(`${src}sports`);
      
      const res = await fetch(`${src}${playerid}/limits/sport`, { headers })
      const data:OmPlayerLimits = await res.json().catch(err => console.log(err));
      if(res.ok){
        sportsLimits = {...data};
      } 
    } catch (error) {
      console.error(error)
    } finally {
      isLoading = false;
    }
  };

  const fetchPaymentLimitRules = async (src, { ...headers }) => {
    try {
      const res = await fetch(src, { headers: { ...headers } });
      const { items } = await res.json();

      if(res.ok) {
        paymentRules = [ ...items ];
      } else {
        console.log('not ok');
      }

    } catch (err) {
      throw new Error(`Api failed with: ${err}`);
    }
  };

  const fetchSaveLimit = async (
    src: string, 
    playerid: string, 
    headers: any, 
    endpoint: string, 
    limitData: any,
    type
  ) => {
    try {
      window.postMessage({type:"LimitSavedInit"},"*");
      const srcV2 = getV2Url(src);
      const res = await fetch(`${srcV2}${playerid}/${endpoint}`, {
        method: 'PUT',
        headers: { ...headers },
        body: JSON.stringify({ ...limitData })
      })
      const data = await res.json().catch(err => console.log(err));
      if(res.ok){
        if(data.error){
          reqSaveError = data.error;
        } else {
          window.postMessage({ type: "LimitSavedSuccesfully" }, "*");
          (type === 'general' || type === 'sports') ? 
            reqSuccesfullLimit = type : 
            reqSuccesfullLimit = {...limitData}
          ;
          reqSaveError = '';
        }
      }else{
        throw new Error(`Failed to save limit`);
      }
    } catch (error) {
      console.error('Error:', error);
      reqSuccesfullLimit = null;
      window.postMessage({ type: "LimitSavedFailed" }, "*");
    }
  };

  const handleSaveLimit = (e, type) => {
    if(type === 'user-limit') {
      let data = e.detail;
      reqSuccesfullLimit = null;
      fetchSaveLimit(
        src, 
        playerid, 
        authHeaders, 
        'limits', 
        data,
        type
      );
    };

    if (type === 'payment-limit') {
      let data = e.detail;
      fetchSaveLimit(
        src, 
        playerid, 
        authHeaders, 
        'paymentLimits', 
        data,
        type
      );
    };

    if (type === 'sport-limit') {
      let data = e.detail;
      fetchSaveLimit(
        src, 
        playerid, 
        authHeaders, 
        'limits/sport',
        data.limits,
        data.type
      );
    };
  };

  const handleEvents = (e) => {
    // Set Auth Headers
    e.data?.type === "SetAuthHeaders" && 
      JSON.stringify(authHeaders) !== JSON.stringify(e.data.payload) && 
      (authHeaders = { ...e.data.payload })
    ;
  };

  const setInitialCurrentTab = async () => {
    await getPemissions();
    if (permissions[0]) {
      currentTab = tabs[0];
    } else if (permissions[1]) {
      currentTab = tabs[1];
    } else if (permissions[2]) {
      currentTab = tabs[2];
    } else {
      currentTab = null;
    }
  }

  onMount(() => {
    window.postMessage({ type: "GetAuthHeaders" }, "*");
    window.addEventListener("message", handleEvents);
    return () => {
      window.removeEventListener("message", handleEvents);
    }
  });

  const getPemissions = async () => {
    [ permissions[0], 
      permissions[1], 
      permissions[2]] = await Promise.all([
        isPermissionPresent(src, 'upp.dashboard.limits.user-limits', 'WRITE'), 
        isPermissionPresent(src, 'upp.dashboard.limits.payment-limits', 'WRITE'), 
        isPermissionPresent(src, 'upp.dashboard.limits.sport-limits', 'WRITE'), 
      ]);
  }

  $: src && setInitialCurrentTab();
  $: src && 
    authHeaders && 
    playerid &&
    fetchPaymentLimitRules(`${src}${playerid}/limitRules`, { ...authHeaders }) &&
    fetchUserLimits(`${getV2Url(src)}${playerid}/limits`, { ...authHeaders }) &&
    fetchSportsLimits(src, playerid, {...authHeaders});
</script>


<section class="tabbed">
  <header class="header">
    <ul>
      {#each tabs as tab, i}
        {#if permissions[i]}
          <li>
            <a 
              href="/"
              on:click|preventDefault={(e) => handleTab(e)}
              class="tabbed__link"
              class:active={currentTab === tab}
            >
              {tab}
            </a>
          </li>
        {/if}  
      {/each}
    </ul>
  </header>

  <div class="container">
    <!-- User Limits -->
    <div class="tabbed__content" class:active={currentTab === tabs[0]}>
      {#if userLimits && userLimits?.hasOwnProperty('selfExclusion')}
        <p>Self exclusion is active</p>
      {:else}
        <!-- Deposit Limit -->
        <deposit-limit
        on:change={(e) => handleSaveLimit(e, 'user-limit')}
        currency={currency}
        success={reqSuccesfullLimit}
        error={reqSaveError}
        />
        <!-- Cumulative Session Limit -->
        <cumulative-limit 
          on:change={(e) => handleSaveLimit(e, 'user-limit')}
          success={reqSuccesfullLimit}
          error={reqSaveError}
        />
        <!-- Wagering Limit -->
        <wagering-limit
          on:change={(e) => handleSaveLimit(e, 'user-limit')}
          currency={currency}
          success={reqSuccesfullLimit}
          error={reqSaveError}
        />
        <!-- Loss Limit -->
        <loss-limit
          on:change={(e) => handleSaveLimit(e, 'user-limit')}
          currency={currency}
          success={reqSuccesfullLimit}
          error={reqSaveError}
        />
        <!-- Session Limit -->
        <session-limit 
          on:change={(e) => handleSaveLimit(e, 'user-limit')}
          success={reqSuccesfullLimit}
          error={reqSaveError}
        />
        <!-- Max Stake -->
        <max-stake
          on:change={(e) => handleSaveLimit(e, 'user-limit')}
          currency={currency}
          success={reqSuccesfullLimit}
          error={reqSaveError}
        />
        <!-- Self Exclusion -->
        <self-exclusion 
          on:change={(e) => handleSaveLimit(e, 'user-limit')}
          success={reqSuccesfullLimit}
          error={reqSaveError}
        />
        <!-- Coll Off -->
        <cool-off
          on:change={(e) => handleSaveLimit(e, 'user-limit')}
          success={reqSuccesfullLimit}
        />
      {/if}
    </div>

    <!-- Payment Limits -->
    <div class="tabbed__content" class:active={currentTab === tabs[1]}>
      <!-- Payment Limit -->
      <payment-limit
        on:change={(e) => handleSaveLimit(e, 'payment-limit')}
        rules={paymentRules}
        success={reqSuccesfullLimit}
        error={reqSaveError}
      />
    </div>

    <!-- Sports Limits -->
    <div class="tabbed__content" class:active={currentTab === tabs[2]}>
      <sport-limit-manage
        on:change={e => handleSaveLimit(e, 'sport-limit')}
        limits={sportsLimits}
        success={reqSuccesfullLimit}
        error={reqSaveError}
        sports={allSports}
      />
    </div>
  </div>
</section>


<style lang="scss">
  *,
  *::before,
  *::after {
    margin: 0;
    padding: 0;
    list-style: none;
    text-decoration: none;
    outline: none;
    box-sizing: border-box;
    font-family: "Helvetica Neue", "Helvetica", sans-serif;
  }

  .tabbed {
    padding: 10px;
    background: #fff;
    border-radius: 2px;
    box-shadow: #bcc0c7 0px 0px 6px;

    header {
      display: flex;
      height: 100%;
      padding: 0 10px;
      background: #fff;
    }

    .container {
      padding-top: 20px;
    }

    ul { 
      display: flex;
      overflow-y: auto;
    }

    li {
      margin-right: 20px;
      &:last-child { margin-right: 0; }
    }

    &__link {
      display: flex;
      padding: 10px 0 10px 0;
      background: #fff;
      color: #000;
      font-size: 14px;

      &.active {
        color: #307fe2;
        border-bottom: 2px solid #307fe2;
      }
    }

    &__content {
      display: none;
      flex-direction: column;
      padding: 10px;
      color: #000;
      &.active { display: flex; }
    }
  }
</style>
